// Account (avatar) menu — the header avatar opens the shared house
// ContextDropdown (`root.contextDropdown`) instead of a bespoke dropdown, so it
// reuses the EXACT /files context-menu shape + styles (MenuPanel /
// MenuPanelItem). `accountMenuItems` builds the rows; `accountMenuAction`
// dispatches. Mirror of filesContextItems / filesContextAction.
import { signoutAndRedirect } from './signout.js'
import { stripWorkspacePrefix } from './workspacePrefix.js'

// The home dashboard's path, with or without the workspace prefix.
const _isHomePath = (path) => {
  if (path === '/') return true
  if (typeof path !== 'string' || !path.startsWith('/w/')) return false
  const rest = stripWorkspacePrefix(path)
  return rest === '/' || rest === ''
}

// itemsFn — called as `el.call('accountMenuItems', root)` (this = the Menu el).
export const accountMenuItems = function (root) {
  const cm = root && root.contextDropdown
  if (!cm || !cm.open) return []
  const items = []
  // Admin gate evaluated AT OPEN. The shell's /admin pages (and the
  // launcher's Admin group) open for every workspace owner and admin
  // (`currentUserIsAdmin`), so the row does too — and for a superadmin
  // (persona round 5, tomas: Admin had no normal entry). Both functions are
  // absent outside the workspace shell (el.call answers undefined) or may
  // throw; each check is guarded on its own and reads false then. One row.
  // The root state goes in as the argument (roleHelpers.js reads it first).
  const asks = (name) => {
    try {
      return !!this.call(name, root)
    } catch (_) {
      return false
    }
  }
  if (asks('currentUserIsAdmin') || asks('currentUserIsSuperadmin')) {
    items.push({ key: 'admin', action: 'admin', label: 'shell.accountMenu.admin' })
  }
  items.push({ key: 'profile', action: 'profile', label: 'shell.accountMenu.profile' })
  items.push({ key: 'settings', action: 'settings', label: 'shell.accountMenu.settings' })
  // Setup belongs to the workspace shell. Other shared-header hosts do not
  // register the replay flow and must not offer an inert menu entry.
  if (typeof this.context?.functions?.onboardingReplay === 'function') {
    items.push({
      key: 'onboarding',
      action: 'onboarding',
      label: 'shell.accountMenu.replayOnboarding'
    })
  }
  // "Customize dashboard" - only on the home dashboard (its action toggles the
  // home customize mode). Moved here from the floating on-page button (2026-06-22
  // per Nika "move FloatingCustomizeBtn into navbar dropdown").
  // The home is `/` once the workspace prefix is off: in the workspace shell
  // the address is `/w/<org>/<workspace>`, and the bare `=== '/'` test never
  // matched it — measured on dev 586122071 (DESIGN-SHELL-RESPONSIVE-IOS-SAFARI-1):
  // the avatar menu at /w/dfli/dfi listed profile, settings, theme and sign
  // out, no "Customize dashboard", so a touch screen's one visible way into
  // edit mode was gone.
  if (root && _isHomePath(root.currentPath)) {
    items.push({
      key: 'customize',
      action: 'customize',
      label: 'shell.accountMenu.customizeDashboard'
    })
  }
  // Theme — ONE explicit choice, never a blind cycle (white-label redesign,
  // DESIGN_SPEC §9 "Avatar menu"): the row names the current mode ("Theme:
  // dark") and opens the three modes, the current one checked. A pick is
  // terminal — see accountMenuAction's `themeMode` branch.
  //
  // The mode words are lowercase in the bundle because they sit inside
  // "Theme: {mode}"; as rows they take sentence case the way CSS
  // `text-transform` would — never for Georgian Mkhedruli, which
  // `toUpperCase()` turns into Mtavruli capitals (AppMicroLabel's note,
  // georgianEyebrowsStayMkhedruli.test.js). The three keys are written out,
  // never concatenated (docs/I18N-CONVENTION.md §1.3).
  const current = (root && root.themeMode) || 'auto'
  const rowCase = (word) => {
    const w = String(word || '')
    return /^[\u10D0-\u10FF]/.test(w) ? w : w.charAt(0).toUpperCase() + w.slice(1)
  }
  const modes = [
    { mode: 'auto', key: 'shell.theme.mode.auto' },
    { mode: 'light', key: 'shell.theme.mode.light' },
    { mode: 'dark', key: 'shell.theme.mode.dark' }
  ]
  items.push({
    key: 'theme',
    label: this.call('labelForTheme', current),
    children: modes.map((m) => ({
      key: `theme-${m.mode}`,
      action: 'themeMode',
      mode: m.mode,
      label: rowCase(this.call('polyglot', m.key)),
      checked: current === m.mode
    }))
  })
  // Language — the WORKSPACE's language, where a person looks for it
  // (persona audit WL-2: it sat four taps deep, name → Admin → Workspace
  // Settings → Language). The workspace shell builds the row
  // (`workspaceLanguageMenuRow`: owner/admin only, the shipped languages by
  // endonym, the current one checked — the Theme row's shape) and applies a
  // pick at once (`workspaceLanguageSelect`, accountMenuAction below); a host
  // that registers neither shows no row, and the shell answers null for a
  // member (the workspace's language is its admins' choice).
  const langFns = this.context?.functions
  if (
    typeof langFns?.workspaceLanguageMenuRow === 'function' &&
    typeof langFns?.workspaceLanguageSelect === 'function'
  ) {
    const langRow = this.call('workspaceLanguageMenuRow', root)
    if (langRow && Array.isArray(langRow.children) && langRow.children.length) items.push(langRow)
  }
  // Human / Agent is a workspace-shell preference. Keep it in the avatar
  // menu on every route, including phones; other shared-header hosts omit it.
  const modeFns = this.context?.functions
  if (
    typeof modeFns?.workspaceModeShown === 'function' &&
    typeof modeFns?.workspaceModeOf === 'function' &&
    typeof modeFns?.workspaceModeSelect === 'function' &&
    this.call('workspaceModeShown', root)
  ) {
    const mode = this.call('workspaceModeOf', root)
    items.push({
      key: 'workspace-mode',
      label: 'workspace.agentMode.switchLabel',
      children: [
        {
          key: 'mode-human',
          action: 'workspaceMode',
          mode: 'ui',
          label: 'workspace.agentMode.ui',
          checked: mode === 'ui'
        },
        {
          key: 'mode-agent',
          action: 'workspaceMode',
          mode: 'cli',
          label: 'workspace.agentMode.cli',
          checked: mode === 'cli'
        }
      ]
    })
  }
  // Navbar — where the navbar sits (Nika 2026-10-24: "make sidebar optional to
  // be positioned at the top left right or bottom"; navbarLayout.js beside
  // this file): the Theme row's shape — the row names the
  // person's place ("Navbar: left") and opens the four, theirs checked. A
  // phone keeps its own header whatever the choice, so it shows no row; nor
  // does a host whose frame cannot place the navbar yet (`placeable`).
  const fns = this.context && this.context.functions
  const nav =
    fns && typeof fns.navbarLayoutRead === 'function' ? this.call('navbarLayoutRead', root) : null
  if (nav && nav.placeable && !nav.phone) {
    const places = [
      { position: 'top', key: 'shell.navbar.position.top' },
      { position: 'left', key: 'shell.navbar.position.left' },
      { position: 'right', key: 'shell.navbar.position.right' },
      { position: 'bottom', key: 'shell.navbar.position.bottom' }
    ]
    const mine = places.find((p) => p.position === nav.position) || places[0]
    items.push({
      key: 'navbar',
      label: this.call('polyglot', 'shell.navbar.label').replace(
        '{position}',
        this.call('polyglot', mine.key)
      ),
      children: places.map((p) => ({
        key: `navbar-${p.position}`,
        action: 'navbarPosition',
        position: p.position,
        label: rowCase(this.call('polyglot', p.key)),
        checked: nav.position === p.position
      }))
    })
  }
  items.push({
    key: 'signout',
    action: 'signout',
    label: 'shell.accountMenu.signOut',
    danger: true
  })
  return items
}

// Opens an account page (/settings, /profile) the way the avatar menu opens
// Settings: the shell's pane opener where the host registers one (2026-09-02,
// Nika: "settings should open in pageslot from top right menu"; a call from
// inside a pane targets that pane), the plain route elsewhere (canvas,
// preview). The host is asked FIRST: `el.call` answers undefined for a name
// the host never registered (smbls element methods.js `call`), so the
// try/catch this replaced never fired and Settings opened nothing there.
// el.call('openAccountPage', path) — the avatar menu and the profile hero.
export function openAccountPage(path) {
  const fns = this && this.context && this.context.functions
  if (fns && typeof fns.openAppPage === 'function') {
    this.call('openAppPage', path, { preferSlot: true })
    return
  }
  this.router(path, this.getRoot())
}

// actionFn — called as `el.call('accountMenuAction', action, item)` from
// ContextDropdownItem.onClick (this = the clicked item el).
export const accountMenuAction = async function (action, item) {
  const root = this.state.root
  // Theme — a mode picked from the Theme submenu (accountMenuItems): apply it
  // through the framework (`changeGlobalTheme` — never a data-theme write),
  // remember it on this device and on the user record, and close the menu.
  if (action === 'themeMode') {
    const next = this.call('normalizeThemeMode', item && item.mode)
    root.update({ themeMode: next, contextDropdown: null }, { preventFetch: true })
    this.call('writeStoredThemeMode', next)
    this.call('changeGlobalTheme', next)
    // Explicit choice → user record too (see ThemeToggle.js / themeMode.js).
    this.call('persistThemeModeToUser', root, this.sdk, next)
    return
  }
  // Navbar — a place picked from the Navbar submenu (accountMenuItems): the
  // person's own look, this host's key (navbarLayout.js beside this file).
  if (action === 'navbarPosition') {
    root.update({ contextDropdown: null }, { preventFetch: true })
    this.call('navbarLayoutSet', { position: item && item.position })
    return
  }
  // Language — a pick from the Language submenu (accountMenuItems): the menu
  // closes, then the workspace shell writes the workspace's language and the
  // session repaints in it at once (persona audit WL-2).
  if (action === 'workspaceLanguage') {
    const fns = this.context?.functions
    if (typeof fns?.workspaceLanguageSelect !== 'function' || !item?.code) return
    root.update({ contextDropdown: null }, { preventFetch: true })
    this.call('workspaceLanguageSelect', item.code)
    return
  }
  if (action === 'workspaceMode') {
    const fns = this.context?.functions
    if (
      typeof fns?.workspaceModeShown !== 'function' ||
      typeof fns?.workspaceModeSelect !== 'function' ||
      !this.call('workspaceModeShown', root) ||
      !['ui', 'cli'].includes(item?.mode)
    )
      return
    root.update({ contextDropdown: null }, { preventFetch: true })
    this.call('workspaceModeSelect', item.mode)
    return
  }
  // Terminal actions — close first, then navigate.
  root.update({ contextDropdown: null }, { preventFetch: true })
  if (action === 'onboarding') {
    this.call('onboardingReplay', 'welcome')
  } else if (action === 'admin') {
    // Open Admin inside the home AppWindow (PageSlot) when on the home
    // dashboard, falling back to a full route elsewhere — same behaviour as
    // every other app launcher (per Nika "admin button in user dropdown should
    // open admin in PageSlot"). openAppPage owns the on-home-vs-route decision;
    // guard for shells that don't register it.
    try {
      this.call('openAppPage', '/admin')
    } catch (_) {
      this.router('/admin', this.getRoot())
    }
  } else if (action === 'customize') {
    // Enter the home dashboard customize mode (slides in the CustomizeDrawer).
    // The menu already closed above. (2026-06-22 per Nika)
    root.update({ _homeCustomizeMode: true }, { preventFetch: true })
  } else if (action === 'settings') {
    // Open Settings inside the PageSlot pane (2026-09-02 per Nika "settings
    // should open in pageslot from top right menu") — same launcher gesture
    // as every other app row. preferSlot: /settings is not in
    // APP_WINDOW_ROUTES (it is a shell page, not a launcher app), and
    // unlike /admin it carries no full-page sidebar nav, so the full-page
    // gate (isFullPageRoute) does not apply. Shells without openAppPage
    // (canvas, preview) keep the plain route — openAccountPage asks the host.
    this.call('openAccountPage', '/settings')
  } else if (action === 'profile') {
    // The workspace's OWN member profile (Nika 2026-10-29: "Own profile /my"):
    // where the host serves /my, the item opens it the way Settings opens
    // /settings (the shell's pane opener, else the plain route). Hosts
    // without /my keep the public profile page, by username, below.
    const hostPages = this.context && this.context.pages
    if (hostPages && hostPages['/my']) {
      this.call('openAccountPage', '/my')
      return
    }
    // FA204 / frank trap — the module-scoped profile-slug cache is genuine
    // cross-call mutable state (the dropdown content can't reliably read
    // page state and getMe is a round-trip, so the second open is
    // instant); re-homed onto `root.__accountMenuProfileSlugCache`
    // (every invocation of this `this`-bound action receives a fresh
    // `this`, but `root` — `this.state.root` — is the stable state
    // object every call shares), same escape hatch as navbarTabMenu.js's
    // `root.__navbarSaveQueue`.
    //
    // Slug must be the Mongo `username` (e.g. `nikoloza`), not the email
    // local-part. Try the schema key, then root.user, then a live getMe.
    let username =
      (root && (root.currentUserUsername || (root.user && root.user.username))) ||
      (root && root.__accountMenuProfileSlugCache) ||
      ''
    if (!username) {
      try {
        const me = await (this.sdk && this.sdk.getMe && this.sdk.getMe())
        const u = (me && (me.data?.user || me.user)) || me || {}
        username = u.username || ''
        if (username && root) root.__accountMenuProfileSlugCache = username
      } catch (err) {
        console.warn('[accountMenu] profile slug getMe failed:', err && err.message)
      }
    }
    // No username yet: the person's own profile where the host serves it.
    // /settings is the person's SETTINGS since the member-profile split;
    // canvas serves /settings only.
    const pages = this.context && this.context.pages
    const own = pages && pages['/profile'] ? '/profile' : '/settings'
    this.router(username ? `/user/${encodeURIComponent(username)}` : own, this.getRoot())
  } else if (action === 'signout') {
    await signoutAndRedirect({ sdk: this.sdk })
  }
}

// Open the account menu, anchored under a given element (A2-14).
//
// Extracted from AppHeader's `Me.onClick` because TWO elements must be able
// to open it and neither may own a copy: the avatar itself, and the wrapper
// around it. The wrapper exists because an Avatar sets `overflow: clip`, so
// the coarse-pointer tap-slop pseudo painted outside its round box is clipped
// away with it — the avatar measured 1 of 5 sample points of a 40x40 finger
// at 390px even with the slop declared. The slop therefore lives on the
// WRAPPER, which clips nothing; and a pseudo belongs to the element that
// originates it, so the enlarged area dispatches on the wrapper. Without this
// function the enlarged target would be a DEAD target.
//
// `anchorEl` is whichever element was tapped; the menu is right-aligned under
// its box. The avatar stops propagation first, so a tap on it opens the menu
// exactly once.
export const openAccountMenu = function (anchorEl) {
  const el = anchorEl || this
  const node = el && el.node
  if (!node || typeof node.getBoundingClientRect !== 'function') return false
  const r = node.getBoundingClientRect()
  const root = this.state?.root || this.state
  if (!root || typeof root.update !== 'function') return false
  // Where the navbar sits (navbarLayout.js beside this file) decides the
  // side the menu opens to: under the avatar at the top (as ever), beside it on
  // a rail, above it at the bottom — the avatar sits at the screen's edge
  // there. A shell without the layout function keeps the top navbar's.
  const fns = this.context && this.context.functions
  const nav =
    fns && typeof fns.navbarLayoutRead === 'function' ? this.call('navbarLayoutRead', root) : null
  const place = (nav && nav.position) || 'top'
  // On a rail the menu starts past the rail's OUTER edge (navbarLayout.js
  // navbarRailRect), not 8px past the avatar: the rail is wider than the
  // avatar, and the menu covered its edge (5px on a narrow rail, measured on
  // dev 579269d80).
  const rail =
    fns && typeof fns.navbarRailRect === 'function' ? this.call('navbarRailRect', el) : null
  const edge = rail || r
  const at =
    place === 'left'
      ? { x: Math.round(edge.right + 8), y: Math.round(r.bottom), fromBottom: true }
      : place === 'right'
        ? {
            x: Math.round(edge.left - 8),
            y: Math.round(r.bottom),
            fromRight: true,
            fromBottom: true
          }
        : place === 'bottom'
          ? { x: Math.round(r.right), y: Math.round(r.top - 8), fromRight: true, fromBottom: true }
          : { x: Math.round(r.right - 200), y: Math.round(r.bottom + 8) }
  root.update(
    {
      contextDropdown: {
        open: true,
        ...at,
        itemsFn: 'accountMenuItems',
        actionFn: 'accountMenuAction'
      }
    },
    { preventFetch: true }
  )
  return true
}
